/**
 * Web MyProfile Page
 * 
 * Clean composition layer using the Facade pattern.
 * 
 * Architecture:
 * - Uses shared useMyProfilePageFacade from @ideoz/core (zero duplication!)
 * - Injects web-specific implementations (navigation, auth, logout)
 * - Passes facade directly to MyProfileTemplate
 * 
 * Benefits:
 * - 80% less code (160+ lines → ~70 lines)
 * - Single source of truth for logic
 * - Easy to test (mock one facade)
 * - Platform-agnostic template
 */

import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MyProfileTemplate, showToast } from '@ideoz/ui';
import {
  useAppDispatch,
  useAppSelector,
  selectUser,
  logout as logoutAction,
  logoutUser,
  addNewEvent,
  EventType,
  useMyProfilePageFacade,
  removeAccessToken,
  checkVisitor,
} from '@ideoz/core';
import { useAuthDialog } from '../hooks/useAuthDialog';
import { useWebNavigation } from '../hooks/useWebNavigation';

export default function MyProfile(): JSX.Element {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const authDialog = useAuthDialog();

  // ========== Platform-Specific Implementations ==========
  const navigation = useWebNavigation();

  // Adapt authDialog to AuthFacade interface
  const auth = React.useMemo(
    () => ({
      isOpen: authDialog.isOpen,
      mode: authDialog.mode,
      isLoading: authDialog.isLoading,
      openLogin: authDialog.openLogin,
      openRegister: authDialog.openRegister,
      close: authDialog.closeDialog,
    }),
    [authDialog]
  );

  // Logout handler (web-specific). JWT sent via Authorization header; removeAccessToken clears both tokens.
  const handleLogout = useCallback(async () => {
    if (!user) return;
    
    try {
      await logoutUser();
      removeAccessToken();
      dispatch(logoutAction());
      await checkVisitor(dispatch);
      navigate('/');
      addNewEvent(EventType.logOut);
    } catch (error) {
      console.error('Logout error:', error);
      showToast({
        message: 'Logout failed',
        severity: 'error',
      });
    }
  }, [user, dispatch, navigate]);

  const logout = React.useMemo(
    () => ({
      logout: handleLogout,
    }),
    [handleLogout]
  );

  // ========== Shared Facade (100% Reusable) ==========
  const facade = useMyProfilePageFacade({
    navigation,
    auth,
    logout,
    showToast,
  });

  // Guard against null user
  if (!user) {
    return <div>Loading...</div>;
  }

  // ========== Render ==========
  return (
    <MyProfileTemplate
      facade={facade}
      isWeb={true}
      authDialogState={authDialog}
    />
  );
}
